// An online payment in progress (spec/06 §9.3, F-PAY-03): what the player pays and how, and what the
// payment provider has said so far. Shared by Sell's checkout and the wallet's top-up.
//
// The payment's QR is drawn here with the kit's encoder: the player scans it with their phone's camera and
// pays on the platform's checkout page. The intent is polled until the provider answers; the caller is told
// once, when it does, so the sale or the wallet is re-read then. Cancelling abandons the request here and
// at the provider.

import type { PaymentIntent } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, encodeQr, Impact, CheckIcon, OrbitLoader, qrPath, RollingNumber } from '@clavian/ui-web';
import { useEffect, useMemo, useRef, type ReactNode } from 'react';

import { Notice } from '../../ui';
import { isSettled, useCancelPaymentIntent, usePaymentIntent } from './queries';

import './payments.css';

export function OnlinePayment({
  organizationId,
  venueId,
  intent: initial,
  onSettled,
  onAgain,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  readonly intent: PaymentIntent;
  /** Called once, when the provider has answered — captured, failed, cancelled or expired. */
  readonly onSettled: (intent: PaymentIntent) => void;
  /** Leave this request behind to make another, or to take another tender. */
  readonly onAgain: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const polled = usePaymentIntent(organizationId, venueId, initial);
  const cancel = useCancelPaymentIntent(organizationId, venueId);
  const intent = polled.data;

  const told = useRef(false);
  useEffect(() => {
    if (told.current || !isSettled(intent)) return;
    told.current = true;
    onSettled(intent);
  }, [intent, onSettled]);

  const status: ReactNode =
    intent.status === 'failed'
      ? intent.failureReason === null
        ? t('onlinePayment.status.failedNoReason')
        : t('onlinePayment.status.failed', { reason: intent.failureReason })
      : t(`onlinePayment.status.${intent.status}`);

  return (
    <section className="console-online-payment" aria-label={t('onlinePayment.label')}>
      <div className="console-checkout__due">
        <span className="console-eyebrow">{t('onlinePayment.amount')}</span>
        <span className="console-checkout__amount clav-figure">
          <RollingNumber value={intent.amount.amount} format={(amount) => format.money({ amount, currency: intent.amount.currency })} />
        </span>
      </div>
      {intent.status === 'pending' ? (
        <>
          <p className="console-subtle">{t('onlinePayment.scan')}</p>
          <PaymentQr payload={intent.qrPayload} label={t('onlinePayment.qrLabel', { amount: format.money(intent.amount) })} />
          {intent.upiIntentUrl === '' ? null : (
            <dl className="console-online-payment__codes">
              <div>
                <dt>{t('onlinePayment.upiLink')}</dt>
                <dd>
                  <a href={intent.upiIntentUrl} className="console-online-payment__code">
                    {intent.upiIntentUrl}
                  </a>
                </dd>
              </div>
            </dl>
          )}
          <p className="console-subtle">{t('onlinePayment.expires', { time: format.time(intent.expiresAt) })}</p>
          {/* Calm waiting: the loader orbits (still under reduced motion) until the provider answers. */}
          <OrbitLoader size={32} label={t('onlinePayment.waiting')} showLabel className="console-online-payment__waiting" />
        </>
      ) : null}
      {intent.status === 'succeeded' ? (
        <div className="console-online-payment__burst" aria-hidden="true">
          <Impact burst="paid" size={64}>
            <CheckIcon width={28} height={28} />
          </Impact>
        </div>
      ) : null}
      <Notice tone={intent.status === 'succeeded' ? 'ok' : intent.status === 'pending' ? 'info' : 'warn'} role="status">
        {status}
      </Notice>
      {intent.status === 'pending' ? (
        <Button variant="secondary" block busy={cancel.isPending} onClick={() => cancel.mutate(intent.id)}>
          {t('onlinePayment.cancel')}
        </Button>
      ) : intent.status === 'succeeded' ? null : (
        <Button variant="secondary" block onClick={onAgain}>
          {t('onlinePayment.again')}
        </Button>
      )}
    </section>
  );
}

/** The payment's QR, dark on white with a quiet zone so any phone camera reads it (as the counter's UPI QR is drawn). */
function PaymentQr({ payload, label }: { readonly payload: string; readonly label: string }): ReactNode {
  const code = useMemo(() => {
    try {
      return encodeQr(payload);
    } catch {
      return null;
    }
  }, [payload]);
  if (code === null) return null;
  return (
    <span className="console-online-payment__qr" role="img" aria-label={label}>
      <svg viewBox={`-4 -4 ${code.size + 8} ${code.size + 8}`} shapeRendering="crispEdges" aria-hidden="true" focusable="false">
        <rect x="-4" y="-4" width={code.size + 8} height={code.size + 8} fill="#fff" />
        <path d={qrPath(code)} fill="#000" />
      </svg>
    </span>
  );
}
